import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  FileText,
  Link2,
  Lock,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectNoneItem,
  SelectTrigger,
  SelectValue,
  clearable,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import ProductCombobox, {
  type ProductOption,
} from "@/components/ProductCombobox";
import EntityCombobox from "@/components/EntityCombobox";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import QuotationPrint, {
  type QuotationPrintData,
} from "@/components/QuotationPrint";
import { formatNumber } from "@/lib/format";
import type { PdfCompany, PdfQuotation } from "@/lib/pdf";
import { renderReactToPdf, safePdfFilename } from "@/lib/htmlPdf";
import { renderQuotationTerms } from "@/lib/quotationTerms";
import {
  DEFAULT_QUOTE_STATUS,
  QUOTE_STATUSES,
  QUOTE_STATUS_LABELS,
  coerceQuoteStatus,
  isQuoteStatus,
  quoteStatusLabel,
  type QuoteStatus,
} from "@/lib/quoteStatus";
import { findDuplicateProductRowIndex } from "@/lib/quotationProductDup";

interface Customer {
  id: number;
  name: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
}

// Estimator / Attention pickers reuse the customer shape — server splits
// the `customers` table into three lists by `contact_type` and returns
// the role-specific entries in dedicated arrays so the form can keep
// each picker scoped to its own role.
interface EntityOption {
  id: number;
  name: string;
  phone?: string | null;
  address?: string | null;
}

interface Product {
  id: number;
  name: string;
  unit: string;
  unit_price: number;
  tax_rate?: number;
}

interface FormSupportResponse {
  customers: Customer[];
  estimators?: EntityOption[];
  attentions?: EntityOption[];
  products: Product[];
  gauges?: string[];
  profiles?: string[];
  colors?: string[];
  product_total?: number;
}

interface LengthQtyEntry {
  length: number;
  quantity: number;
}

interface Item {
  productId: number | null;
  unit: string;
  productName: string;
  quantity: number;
  rate: number;
  amount: number;
  rateLocked: boolean;
  lengthBreakdown: LengthQtyEntry[];
}

interface QuotationApi {
  id: number;
  quote_number: string;
  number?: string;
  created_by_name?: string | null;
  customer_id: number | null;
  customer_name: string;
  telephone: string;
  location: string;
  project_type: string;
  status: string;
  pfi_date: string | null;
  registered_customer: boolean;
  // Optional Entity contacts — added with migration 0018. Server returns
  // both the FK and the dereferenced display name so the form can render
  // immediately without a follow-up lookup.
  estimator_customer_id?: number | null;
  estimator_name?: string | null;
  attention_customer_id?: number | null;
  attention_name?: string | null;
  // Required quotation-level attributes added with migration 0019. The
  // server returns null for rows that pre-date the migration so the form
  // shows an empty Select until the user picks each value.
  gauge?: string | null;
  profile?: string | null;
  color?: string | null;
  markup_percent: number;
  subtotal: number;
  discount_percent: number;
  discount_amount: number;
  taxable_amount: number;
  nhil: number;
  getfund: number;
  vat: number;
  transportation: number;
  installation_percent: number;
  installation_amount: number;
  net_total: number;
  total_amount: number;
  notes: string;
  locked_by_waybill: boolean;
  items: Array<{
    id?: number;
    line_no?: number;
    product_id: number | null;
    product_name: string;
    unit: string;
    quantity: number;
    rate: number;
    amount: number;
    length_breakdown: LengthQtyEntry[];
    rate_locked: boolean;
  }>;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// Quotation numbers are now auto-generated server-side (see
// `nextQuoteNumber` in api/src/routes/quotations.ts) on first save. The form
// keeps an empty placeholder until the server returns the assigned number.
const AUTO_QUOTE_NUMBER_PLACEHOLDER = "Auto-generated on save";
const FORM_SUPPORT_PAGE_SIZE = 1000;

// Fetch every customer + product the form needs to render its pickers
// through the document-scoped form-support endpoint. This endpoint is gated
// by quotes.read OR quotes.write, so a user with only Quotations granted
// can still populate the form — they don't need Customers / Products too.
async function loadQuotationFormSupport(): Promise<{
  customers: Customer[];
  estimators: EntityOption[];
  attentions: EntityOption[];
  products: Product[];
  gauges: string[];
  profiles: string[];
  colors: string[];
}> {
  const customers: Customer[] = [];
  const estimators: EntityOption[] = [];
  const attentions: EntityOption[] = [];
  const products: Product[] = [];
  let gauges: string[] = [];
  let profiles: string[] = [];
  let colors: string[] = [];
  let offset = 0;
  let productTotal = 0;
  let entitiesLoaded = false;

  while (true) {
    const params = new URLSearchParams({
      limit: String(FORM_SUPPORT_PAGE_SIZE),
      offset: String(offset),
    });
    const res = await api.get<FormSupportResponse>(
      `/quotations/form-support?${params.toString()}`,
    );
    if (!entitiesLoaded) {
      customers.push(...(res.customers ?? []));
      estimators.push(...(res.estimators ?? []));
      attentions.push(...(res.attentions ?? []));
      gauges = (res.gauges ?? []).map((v) => String(v ?? "").trim()).filter(Boolean);
      profiles = (res.profiles ?? []).map((v) => String(v ?? "").trim()).filter(Boolean);
      colors = (res.colors ?? []).map((v) => String(v ?? "").trim()).filter(Boolean);
      entitiesLoaded = true;
    }
    const page = res.products ?? [];
    products.push(...page);
    productTotal = Number(res.product_total ?? products.length);

    if (page.length < FORM_SUPPORT_PAGE_SIZE || products.length >= productTotal) {
      break;
    }
    offset += page.length;
  }

  return { customers, estimators, attentions, products, gauges, profiles, colors };
}

function cloneBreakdown(rows: LengthQtyEntry[] | undefined): LengthQtyEntry[] {
  if (!Array.isArray(rows) || !rows.length) {
    return [{ length: 0, quantity: 0 }];
  }
  return rows.map((r) => ({
    length: Number(r.length ?? 0),
    quantity: Number(r.quantity ?? 0),
  }));
}

function computeBreakdownQty(rows: LengthQtyEntry[]) {
  return rows.reduce(
    (sum, r) => sum + Number(r.length || 0) * Number(r.quantity || 0),
    0,
  );
}

function itemNeedsLengthQty(it: Item, catalog: Product[]) {
  if (it.productId) return catalog.some((p) => p.id === it.productId);
  return false;
}

function getBreakdownValidationMessage(
  items: Item[],
  catalog: Product[],
): string | null {
  for (let i = 0; i < items.length; i++) {
    const it = items[i]!;
    if (!itemNeedsLengthQty(it, catalog)) continue;
    const rows = it.lengthBreakdown.length
      ? it.lengthBreakdown
      : [{ length: 0, quantity: 0 }];
    const active = rows.filter(
      (r) => Number(r.length) !== 0 || Number(r.quantity) !== 0,
    );
    const label = it.productName || "product";
    if (active.length === 0) {
      return `Line ${i + 1} (${label}): open the length × quantity calculator and enter values greater than zero.`;
    }
    if (active.some((r) => Number(r.length) <= 0 || Number(r.quantity) <= 0)) {
      return `Line ${i + 1} (${label}): every length and quantity must be greater than zero.`;
    }
    if (computeBreakdownQty(rows) <= 0) {
      return `Line ${i + 1} (${label}): total quantity must be greater than zero.`;
    }
  }
  return null;
}

function isCatalogBreakdownInvalid(it: Item, catalog: Product[]) {
  return getBreakdownValidationMessage([it], catalog) !== null;
}

const BLANK_ITEM: Item = {
  productId: null,
  unit: "Pieces",
  productName: "",
  quantity: 0,
  rate: 0,
  amount: 0,
  rateLocked: true,
  lengthBreakdown: [{ length: 0, quantity: 0 }],
};

export default function QuotationForm() {
  const { id } = useParams<{ id: string }>();
  const { pathname, search } = useLocation();
  const nav = useNavigate();
  const { user, can, isFeatureEnabled } = useAuth();
  const taxEnabled = isFeatureEnabled("tax");
  const canEdit = can("quotes.write");
  const isExisting = Boolean(id);
  const isPreview = Boolean(id && !pathname.endsWith("/edit"));
  const wantsAutoOpenPdf = useMemo(
    () => new URLSearchParams(search).get("openPdf") === "1",
    [search],
  );
  const [lockedByWaybill, setLockedByWaybill] = useState(false);
  const lock = isPreview || lockedByWaybill || !canEdit;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [estimators, setEstimators] = useState<EntityOption[]>([]);
  const [attentions, setAttentions] = useState<EntityOption[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [gaugeOptions, setGaugeOptions] = useState<string[]>([]);
  const [profileOptions, setProfileOptions] = useState<string[]>([]);
  const [colorOptions, setColorOptions] = useState<string[]>([]);
  const [company, setCompany] = useState<PdfCompany>({});
  const [quotationTerms, setQuotationTerms] = useState<string | null>(null);

  const [quotationNumber, setQuotationNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [telephone, setTelephone] = useState("");
  // The legacy "Registered Customer" toggle is gone — the form now always
  // picks from saved Entities (contact_type='customer'), so this flag is
  // hard-coded to true so existing rows keep round-tripping cleanly.
  const [estimatorName, setEstimatorName] = useState("");
  const [estimatorId, setEstimatorId] = useState<number | null>(null);
  const [attentionName, setAttentionName] = useState("");
  const [attentionId, setAttentionId] = useState<number | null>(null);
  const [pfiDate, setPfiDate] = useState(todayIso);
  const [location, setLocation] = useState("");
  const [projectType, setProjectType] = useState("Actual");
  const [status, setStatus] = useState<QuoteStatus>(DEFAULT_QUOTE_STATUS);
  // Optional quotation-level attributes (Gauge / Profile / Color). Stored
  // as plain strings — "" means unset and is persisted as null on save.
  const [gauge, setGauge] = useState("");
  const [profile, setProfile] = useState("");
  const [color, setColor] = useState("");
  const [markupPercent, setMarkupPercent] = useState(0);
  const [items, setItems] = useState<Item[]>([{ ...BLANK_ITEM }]);
  const [notes, setNotes] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [transportation, setTransportation] = useState(0);
  // Installation is captured in TWO linked fields so users can pick whichever
  // is more convenient:
  //   - `installationPercent` — percent of the taxable amount.
  //   - `installationAmount`  — direct money figure.
  // Editing one updates the other (see setInstallationPct / setInstallationAmt
  // below) and `installationPercent` is reset to 0 when the user types a
  // direct amount so the two values never disagree.
  const [installationPercent, setInstallationPercent] = useState(0);
  const [installationAmount, setInstallationAmount] = useState(0);
  // Which input drives installation: "percent" derives the amount from the
  // subtotal; "amount" lets the user type a flat figure directly (percent is
  // pinned to 0). The mode gates the recompute effect below so a manually
  // entered amount is never overwritten when the subtotal changes.
  const [installationMode, setInstallationMode] = useState<
    "percent" | "amount"
  >("percent");
  const [createdByName, setCreatedByName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  // When true, every row missing a catalog `productId` lights up red and a
  // helper sentence appears under the combobox. We only flip this on Save
  // attempts so users aren't yelled at while they're still typing.
  const [showProductErrors, setShowProductErrors] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState("");
  const [pdfQuoteNumber, setPdfQuoteNumber] = useState("");
  const [postSaveNavId, setPostSaveNavId] = useState<number | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [autoOpenConsumed, setAutoOpenConsumed] = useState(false);
  const [activeNumberField, setActiveNumberField] = useState<string | null>(
    null,
  );
  // Briefly highlight the row that already holds a duplicate product so the
  // user can see WHICH existing row blocked the new pick. The ref maps a row
  // index to a wrapping <div data-line-row> we can scroll into view and the
  // state value drives the temporary highlight class.
  const itemRowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [duplicateHighlightIdx, setDuplicateHighlightIdx] = useState<
    number | null
  >(null);

  const displayNumber = (fieldId: string, value: number) =>
    activeNumberField === fieldId && Number(value) === 0 ? "" : String(value);

  const baseSubtotal = useMemo(
    () => items.reduce((sum, it) => sum + Number(it.amount || 0), 0),
    [items],
  );
  const markupAmount = baseSubtotal * (Number(markupPercent || 0) / 100);
  const subtotal = baseSubtotal + markupAmount;
  const discountAmount = subtotal * (discountPercent / 100);
  const taxableAmount = Math.max(0, subtotal - discountAmount);
  // When the deployment's `features.tax` flag is OFF all Ghanaian levies are
  // forced to zero so the live total mirrors the (also-gated) server total and
  // the document stays tax-free. The server is still the authoritative guard.
  const nhil = taxEnabled ? taxableAmount * 0.025 : 0;
  const getfund = taxEnabled ? taxableAmount * 0.025 : 0;
  const vat = taxEnabled ? taxableAmount * 0.15 : 0;
  const netTotal = taxableAmount + nhil + getfund + vat;
  const total = netTotal + transportation + Number(installationAmount || 0);

  const gaugeSelectOptions = useMemo(() => {
    const base = gaugeOptions.map((value) => ({ value, label: value }));
    const legacy = gauge.trim();
    if (legacy && !gaugeOptions.includes(legacy)) {
      base.push({ value: legacy, label: `${legacy} (legacy)` });
    }
    return base;
  }, [gaugeOptions, gauge]);

  const profileSelectOptions = useMemo(() => {
    const base = profileOptions.map((value) => ({ value, label: value }));
    const legacy = profile.trim();
    if (legacy && !profileOptions.includes(legacy)) {
      base.push({ value: legacy, label: `${legacy} (legacy)` });
    }
    return base;
  }, [profileOptions, profile]);

  const colorSelectOptions = useMemo(() => {
    const base = colorOptions.map((value) => ({ value, label: value }));
    const legacy = color.trim();
    if (legacy && !colorOptions.includes(legacy)) {
      base.push({ value: legacy, label: `${legacy} (legacy)` });
    }
    return base;
  }, [colorOptions, color]);

  // Recompute the installation amount whenever either the percent or the
  // subtotal changes. Per spec: Installation Amount = Subtotal × percent.
  // (Subtotal already includes markup but is taken BEFORE discount, NHIL,
  // GETFund, and VAT so the figure stays predictable for the user.)
  // Only runs in "percent" mode — in "amount" mode the user-typed figure is
  // authoritative and must never be clobbered by a subtotal change.
  useEffect(() => {
    if (installationMode !== "percent") return;
    const computed = Number(
      (subtotal * (Number(installationPercent || 0) / 100)).toFixed(2),
    );
    if (Math.abs(computed - Number(installationAmount || 0)) > 0.005) {
      setInstallationAmount(computed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installationPercent, subtotal, installationMode]);

  function setInstallationPct(next: number) {
    const clamped = Number.isFinite(next) ? Math.max(0, next) : 0;
    setInstallationPercent(clamped);
    setInstallationAmount(Number((subtotal * (clamped / 100)).toFixed(2)));
  }

  // Direct flat-amount entry. Pins percent to 0 so the two never disagree and
  // the saved quote records the figure as an explicit installation amount.
  function setInstallationAmt(next: number) {
    const clamped = Number.isFinite(next) ? Math.max(0, next) : 0;
    setInstallationPercent(0);
    setInstallationAmount(clamped);
  }

  // Switch entry mode. Going to "amount" keeps the current computed figure as
  // the starting flat amount and clears the percent; going back to "percent"
  // re-derives the amount from whatever percent is set.
  function switchInstallationMode(mode: "percent" | "amount") {
    setInstallationMode(mode);
    if (mode === "amount") {
      setInstallationPercent(0);
    } else {
      setInstallationAmount(
        Number((subtotal * (Number(installationPercent || 0) / 100)).toFixed(2)),
      );
    }
  }

  function resetToNewQuotation() {
    setQuotationNumber("");
    setCustomerName("");
    setCustomerId(null);
    setTelephone("");
    setEstimatorName("");
    setEstimatorId(null);
    setAttentionName("");
    setAttentionId(null);
    setPfiDate(todayIso());
    setLocation("");
    setProjectType("Actual");
    setStatus(DEFAULT_QUOTE_STATUS);
    setGauge("");
    setProfile("");
    setColor("");
    setMarkupPercent(0);
    setItems([{ ...BLANK_ITEM }]);
    setNotes("");
    setDiscountPercent(0);
    setTransportation(0);
    setInstallationPercent(0);
    setInstallationAmount(0);
    setInstallationMode("percent");
    setLockedByWaybill(false);
    setActiveNumberField(null);
    setShowProductErrors(false);
  }

  function startNewQuotation() {
    resetToNewQuotation();
    nav("/quotations/new");
  }

  useEffect(() => {
    if (id) return;
    resetToNewQuotation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // Pickers are populated via the Quotations-scoped form-support endpoint
    // so granting only the Quotations module is enough — the user does not
    // also need Customers or Products. A failure surfaces as a single toast
    // and leaves the dropdowns empty (the customer name + product fields
    // still allow free text).
    (async () => {
      try {
        const data = await loadQuotationFormSupport();
        setCustomers(data.customers);
        setEstimators(data.estimators);
        setAttentions(data.attentions);
        setProducts(data.products);
        setGaugeOptions(data.gauges);
        setProfileOptions(data.profiles);
        setColorOptions(data.colors);
      } catch (err) {
        toast.error(
          (err as Error)?.message ||
            "Failed to load entity and product lists",
        );
      }
    })();
  }, []);

  // Best-effort load of company settings — used to brand the PDF header.
  // Falls back to the BillForge defaults if the user lacks settings.read.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ settings: Record<string, string> }>(
          "/settings",
        );
        const s = res.settings ?? {};
        setCompany({
          name: s["company.name"] || undefined,
          email: s["company.email"] || undefined,
          phone: s["company.phone"] || undefined,
          address: s["company.address"] || undefined,
          currency_code: s["currency.code"] || undefined,
          currency_symbol: s["currency.symbol"] || undefined,
        });
        // `quotation.terms` is stored as a single TEXT row in app_settings.
        // Keep null when the key isn't present so the print renderer can
        // fall back to the bundled DEFAULT_QUOTATION_TERMS string instead
        // of an empty section.
        const rawTerms = s["quotation.terms"];
        setQuotationTerms(
          typeof rawTerms === "string" && rawTerms.length > 0 ? rawTerms : null,
        );
      } catch {
        // Non-fatal: PDF will still render with default branding and the
        // bundled default Terms & Conditions text.
      }
    })();
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoadingDoc(true);
    (async () => {
      try {
        const data = await api.get<{ quotation: QuotationApi }>(
          `/quotations/${id}`,
        );
        const q = data.quotation;
        setLockedByWaybill(!!q.locked_by_waybill);
        if (q.locked_by_waybill && pathname.endsWith("/edit")) {
          nav(`/quotations/${id}`, { replace: true });
          toast.error("This quotation is locked — a waybill has been generated.");
        }
        setQuotationNumber(q.quote_number || q.number || "");
        setCustomerId(q.customer_id ?? null);
        setCustomerName(q.customer_name || "");
        setTelephone(q.telephone || "");
        setEstimatorId(q.estimator_customer_id ?? null);
        setEstimatorName(q.estimator_name || "");
        setAttentionId(q.attention_customer_id ?? null);
        setAttentionName(q.attention_name || "");
        setPfiDate(q.pfi_date || todayIso());
        setLocation(q.location || "");
        setProjectType(q.project_type || "Actual");
        // Map any stored status into the canonical workflow status. Legacy
        // rows may still carry payment states (paid/partial/pending) written
        // by older receipt code — those are NOT draft, so they must load as
        // Approved, never fall back to Draft.
        setStatus(coerceQuoteStatus(q.status));
        setGauge(typeof q.gauge === "string" ? q.gauge.trim() : "");
        setProfile(typeof q.profile === "string" ? q.profile.trim() : "");
        setColor(typeof q.color === "string" ? q.color.trim() : "");
        setMarkupPercent(Number(q.markup_percent || 0));
        setNotes(q.notes || "");
        setDiscountPercent(Number(q.discount_percent || 0));
        setTransportation(Number(q.transportation || 0));
        // Restore both the percent and the resolved amount. Rows saved before
        // the percent input was reintroduced still have `installation_amount`
        // populated so they continue to render with the correct total.
        setInstallationPercent(Number(q.installation_percent || 0));
        setInstallationAmount(Number(q.installation_amount || 0));
        // Pick the entry mode that matches how the quote was saved: a non-zero
        // percent loads in percent mode; a flat amount with no percent loads in
        // amount mode so editing doesn't re-derive (and overwrite) the figure.
        setInstallationMode(
          Number(q.installation_percent || 0) > 0
            ? "percent"
            : Number(q.installation_amount || 0) > 0
              ? "amount"
              : "percent",
        );
        setCreatedByName(q.created_by_name ?? null);
        const loadedItems: Item[] = (q.items || []).map((it) => {
          const breakdown = cloneBreakdown(it.length_breakdown);
          const quantity =
            computeBreakdownQty(breakdown) || Number(it.quantity || 0);
          const rate = Number(it.rate || 0);
          return {
            productId: it.product_id ?? null,
            productName: it.product_name || "",
            unit: it.unit || "Pieces",
            quantity,
            rate,
            amount: Number(it.amount ?? quantity * rate),
            rateLocked: it.rate_locked !== false,
            lengthBreakdown: breakdown,
          };
        });
        setItems(loadedItems.length ? loadedItems : [{ ...BLANK_ITEM }]);
      } catch (err) {
        toast.error((err as Error).message || "Failed to load quotation");
      } finally {
        setLoadingDoc(false);
      }
    })();
  }, [id, nav, pathname]);

  function updateItem(idx: number, patch: Partial<Item>) {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, ...patch };
        next.amount = Number(next.quantity || 0) * Number(next.rate || 0);
        return next;
      }),
    );
  }

  // Briefly scroll to + flash the row that's blocking a duplicate pick so the
  // user can find it (the ProductCombobox dropdown can otherwise cover the
  // existing row from view). The highlight is cleared after ~2s.
  function flashDuplicateRow(idx: number) {
    setDuplicateHighlightIdx(idx);
    requestAnimationFrame(() => {
      const node = itemRowRefs.current[idx];
      if (node) node.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    window.setTimeout(() => {
      setDuplicateHighlightIdx((current) => (current === idx ? null : current));
    }, 2000);
  }

  function applyProductToRow(idx: number, match: Product | ProductOption) {
    // Each catalog product is allowed at most once per quotation. If the
    // user picks a product that's already on another row we bail out: keep
    // the existing row untouched, leave the duplicating row empty so the
    // user can pick a different product, and flash the original row so the
    // user can find / update it instead. Empty rows (productId == null) are
    // intentionally exempt — the user is allowed multiple unfilled rows.
    const duplicateIdx = findDuplicateProductRowIndex(
      items,
      idx,
      match.id,
    );
    if (duplicateIdx >= 0) {
      // Revert the duplicating row to an empty product picker so the user
      // can immediately try a different product. We deliberately do NOT
      // touch the row that already holds the catalog product.
      setItems((prev) =>
        prev.map((it, i) =>
          i === idx
            ? {
                ...it,
                productId: null,
                productName: "",
              }
            : it,
        ),
      );
      toast.error(
        `Product "${match.name}" is already on this quotation. Update the existing row instead.`,
      );
      flashDuplicateRow(duplicateIdx);
      return;
    }

    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const breakdown = cloneBreakdown(it.lengthBreakdown);
        const hasEntered = breakdown.some(
          (r) => Number(r.length) !== 0 || Number(r.quantity) !== 0,
        );
        const quantity = hasEntered
          ? computeBreakdownQty(breakdown)
          : Number(it.quantity || 0);
        const nextRate = it.rateLocked
          ? Number(match.unit_price || 0)
          : it.rate || Number(match.unit_price || 0);
        return {
          ...it,
          productId: match.id,
          productName: match.name,
          unit: match.unit || "Pieces",
          rate: nextRate,
          quantity,
          amount: Number((quantity * nextRate).toFixed(2)),
          lengthBreakdown: breakdown,
        };
      }),
    );
  }

  function mapSavedQuotationToPdf(q: QuotationApi): PdfQuotation {
    return {
      quote_number: q.quote_number || q.number || quotationNumber,
      pfi_date: q.pfi_date ?? pfiDate,
      customer_name: q.customer_name || customerName,
      telephone: q.telephone || telephone,
      location: q.location || location,
      project_type: q.project_type || projectType,
      status: q.status || status,
      estimator_name: q.estimator_name ?? estimatorName ?? "",
      attention_name: q.attention_name ?? attentionName ?? "",
      // Prefer the server-echoed value; fall back to current form state so
      // the PDF generated immediately after save shows what the user just
      // picked even if the API round-trip somehow strips a field.
      gauge: q.gauge ?? (gauge || null),
      profile: q.profile ?? (profile || null),
      color: q.color ?? (color || null),
      markup_percent: Number(q.markup_percent ?? markupPercent),
      subtotal: Number(q.subtotal ?? subtotal),
      discount_percent: Number(q.discount_percent ?? discountPercent),
      discount_amount: Number(q.discount_amount ?? discountAmount),
      taxable_amount: Number(q.taxable_amount ?? taxableAmount),
      nhil: Number(q.nhil ?? nhil),
      getfund: Number(q.getfund ?? getfund),
      vat: Number(q.vat ?? vat),
      transportation: Number(q.transportation ?? transportation),
      installation_percent: Number(
        q.installation_percent ?? installationPercent,
      ),
      installation_amount: Number(
        q.installation_amount ?? installationAmount,
      ),
      net_total: Number(q.net_total ?? netTotal),
      total_amount: Number(q.total_amount ?? total),
      notes: q.notes || notes,
      items: (q.items || []).map((it, idx) => ({
        line_no: it.line_no ?? idx + 1,
        product_name: it.product_name,
        unit: it.unit,
        quantity: Number(it.quantity || 0),
        rate: Number(it.rate || 0),
        amount: Number(it.amount || 0),
      })),
    };
  }

  function buildPdfFromFormState(): PdfQuotation {
    return {
      quote_number: quotationNumber,
      pfi_date: pfiDate,
      customer_name: customerName,
      telephone,
      location,
      project_type: projectType,
      status,
      estimator_name: estimatorName,
      attention_name: attentionName,
      gauge: gauge || null,
      profile: profile || null,
      color: color || null,
      markup_percent: Number(markupPercent || 0),
      subtotal,
      discount_percent: Number(discountPercent || 0),
      discount_amount: discountAmount,
      taxable_amount: taxableAmount,
      nhil,
      getfund,
      vat,
      transportation: Number(transportation || 0),
      installation_percent: Number(installationPercent || 0),
      installation_amount: Number(installationAmount || 0),
      net_total: netTotal,
      total_amount: total,
      notes,
      items: items.map((it, idx) => ({
        line_no: idx + 1,
        product_name: it.productName,
        unit: it.unit,
        quantity: Number(it.quantity || 0),
        rate: Number(it.rate || 0),
        amount: Number(it.amount || 0),
      })),
    };
  }

  // Adapter — the new HTML-driven PDF renderer wants the same shape used by
  // the old desktop print component (QuotationPrintData). All fields that
  // were computed for the original PdfQuotation map cleanly across.
  function mapPdfToPrint(pdf: PdfQuotation): QuotationPrintData {
    return {
      number: pdf.quote_number,
      pfiDate: pdf.pfi_date ?? null,
      projectType: pdf.project_type,
      customerName: pdf.customer_name,
      telephone: pdf.telephone ?? "",
      location: pdf.location ?? "",
      // `registered` is always true now that the toggle is gone — the form
      // only ever stores quotations against saved Entity rows.
      registered: true,
      estimatorName: pdf.estimator_name ?? "",
      attentionName: pdf.attention_name ?? "",
      gauge: pdf.gauge ?? null,
      profile: pdf.profile ?? null,
      color: pdf.color ?? null,
      items: pdf.items.map((it) => ({
        productName: it.product_name || it.description || "",
        unit: it.unit || "",
        quantity: Number(it.quantity || 0),
        rate: Number(it.rate || 0),
        amount: Number(it.amount || 0),
      })),
      subtotal: Number(pdf.subtotal || 0),
      discount: Number(pdf.discount_percent || 0),
      // Hide every tax line on the PDF when the Tax feature is OFF. NET TOTAL is
      // recomputed by the print component from the visible figures so the
      // document stays internally consistent (total = taxable when tax is off).
      showTax: taxEnabled,
      taxable: Number(pdf.taxable_amount || 0),
      nhilRate: 2.5,
      nhil: Number(pdf.nhil || 0),
      getfundRate: 2.5,
      getfund: Number(pdf.getfund || 0),
      vatRate: 15,
      vat: Number(pdf.vat || 0),
      transportation: Number(pdf.transportation || 0),
      installationRate: Number(pdf.installation_percent || 0),
      installationAmount: Number(pdf.installation_amount || 0),
      total: Number(pdf.total_amount || 0),
      notes: pdf.notes ?? "",
    };
  }

  function resolvePreparedBy(name?: string | null): string {
    const trimmed = name?.trim();
    if (trimmed) return trimmed;
    return (
      user?.name?.trim() ||
      user?.username?.trim() ||
      user?.email?.trim() ||
      "—"
    );
  }

  async function generatePdf(pdf: PdfQuotation, preparedBy: string) {
    const printCompany = {
      name: (company.name || "BillForge").toString(),
      address: (company.address || "").toString(),
      phone: (company.phone || "").toString(),
      email: (company.email || "").toString(),
      currency: (company.currency_code || "GHS").toString(),
    };
    const termsText = renderQuotationTerms(quotationTerms, printCompany.name);
    return renderReactToPdf(
      <QuotationPrint
        q={mapPdfToPrint(pdf)}
        company={printCompany}
        termsText={termsText}
        preparedBy={preparedBy}
      />,
      {
        filename: safePdfFilename("Quotation", pdf.quote_number || "Draft"),
      },
    );
  }

  function openPdfPreview(pdf: { url: string; filename: string }, quoteNo: string) {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(pdf.url);
    setPdfFilename(pdf.filename);
    setPdfQuoteNumber(quoteNo);
    setPdfModalOpen(true);
  }

  function handlePdfModalOpenChange(open: boolean) {
    if (!open) {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
      setPdfFilename("");
      setPdfQuoteNumber("");
      setPdfModalOpen(false);
      if (postSaveNavId != null) {
        nav(`/quotations/${postSaveNavId}`);
        setPostSaveNavId(null);
      }
    } else {
      setPdfModalOpen(true);
    }
  }

  async function handleDownloadPdfFromForm() {
    try {
      const pdf = await generatePdf(
        buildPdfFromFormState(),
        resolvePreparedBy(createdByName),
      );
      const a = document.createElement("a");
      a.href = pdf.url;
      a.download = pdf.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(pdf.url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    }
  }

  async function handlePreviewPdfFromForm() {
    if (generatingPdf) return;
    setGeneratingPdf(true);
    try {
      const pdfData = buildPdfFromFormState();
      const pdf = await generatePdf(pdfData, resolvePreparedBy(createdByName));
      openPdfPreview(pdf, pdfData.quote_number);
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  }

  // Honour the ?openPdf=1 query param from the listing's "Preview PDF" row
  // action — once the quotation has finished loading, auto-open the preview
  // exactly once so refreshes / re-renders don't re-trigger the modal.
  useEffect(() => {
    if (!wantsAutoOpenPdf || autoOpenConsumed) return;
    if (!id || loadingDoc) return;
    if (!quotationNumber) return;
    setAutoOpenConsumed(true);
    void handlePreviewPdfFromForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsAutoOpenPdf, autoOpenConsumed, id, loadingDoc, quotationNumber]);

  function updateBreakdown(
    itemIndex: number,
    rowIndex: number,
    patch: Partial<LengthQtyEntry>,
  ) {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== itemIndex) return it;
        const rows = it.lengthBreakdown.length
          ? [...it.lengthBreakdown]
          : [{ length: 0, quantity: 0 }];
        rows[rowIndex] = { ...rows[rowIndex]!, ...patch };
        const computed = computeBreakdownQty(rows);
        return {
          ...it,
          lengthBreakdown: rows,
          quantity: computed,
          amount: Number((computed * Number(it.rate || 0)).toFixed(2)),
        };
      }),
    );
  }

  function addBreakdownRow(idx: number) {
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx
          ? {
              ...it,
              lengthBreakdown: [...it.lengthBreakdown, { length: 0, quantity: 0 }],
            }
          : it,
      ),
    );
  }

  function removeBreakdownRow(itemIndex: number, rowIndex: number) {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== itemIndex) return it;
        const rows = it.lengthBreakdown.filter((_, ridx) => ridx !== rowIndex);
        const safe = rows.length ? rows : [{ length: 0, quantity: 0 }];
        const computed = computeBreakdownQty(safe);
        return {
          ...it,
          lengthBreakdown: safe,
          quantity: computed,
          amount: Number((computed * Number(it.rate || 0)).toFixed(2)),
        };
      }),
    );
  }

  async function save(opts: { finalise?: boolean } = {}) {
    if (lockedByWaybill) {
      toast.error("This quotation is locked and cannot be edited.");
      return;
    }
    if (isPreview) {
      toast.message("Open via Edit to modify this quotation.");
      return;
    }
    if (!customerId) {
      return toast.error(
        "Select a customer from the saved entities list.",
      );
    }
    if (!location.trim()) return toast.error("Enter project location");
    if (!projectType) return toast.error("Project type is required");

    // Rule 3: every item must point at a product from the catalog. Free-text
    // is allowed *while typing* so the user can search, but Save is blocked
    // until every row resolves to a productId. The matching rows light up
    // red via `showProductErrors`.
    const missingProduct = items.findIndex((it) => !it.productId);
    if (missingProduct >= 0) {
      setShowProductErrors(true);
      toast.error("Please select a product from the list.");
      // Scroll/focus the first unresolved row so the user lands on the
      // problem field immediately. The combobox stamps an empty
      // data-product-id when nothing is picked.
      requestAnimationFrame(() => {
        const firstBad = document.querySelector<HTMLInputElement>(
          'input[data-strict="true"][data-product-id=""]',
        );
        if (firstBad) {
          firstBad.scrollIntoView({ block: "center", behavior: "smooth" });
          firstBad.focus();
        }
      });
      return;
    }
    // Clear the strict-mode flag once every row has a productId.
    if (showProductErrors) setShowProductErrors(false);
    // Rule 2: refuse to save when any line has a non-positive rate or quantity.
    const badLine = items.findIndex(
      (it) => !(Number(it.rate) > 0) || !(Number(it.quantity) > 0),
    );
    if (badLine >= 0) {
      return toast.error(
        "Each item must have a rate and quantity greater than zero",
      );
    }
    const breakdownErr = getBreakdownValidationMessage(items, products);
    if (breakdownErr) return toast.error(breakdownErr);

    const submitStatus: QuoteStatus = opts.finalise ? "approved" : status;
    const payload = {
      // Omit `quote_number` on create so the server assigns the next sequential
      // number (`QTN-YYYY-####`). For updates, keep the existing number.
      quote_number: isExisting ? quotationNumber.trim() || undefined : undefined,
      customer_id: customerId,
      customer_name: customerName.trim(),
      telephone,
      location,
      project_type: projectType,
      status: submitStatus,
      pfi_date: pfiDate || todayIso(),
      // The legacy registered_customer toggle was removed — every quotation
      // now picks from saved Entities so this flag is always true.
      registered_customer: true,
      // New Entity FKs. The server validates that each id references a
      // customers row whose contact_type matches the slot.
      estimator_customer_id: estimatorId,
      attention_customer_id: attentionId,
      gauge: gauge || null,
      profile: profile || null,
      color: color || null,
      markup_percent: Number(markupPercent || 0),
      discount_percent: Number(discountPercent || 0),
      transportation: Number(transportation || 0),
      installation_percent: Number(installationPercent || 0),
      installation_amount: Number(installationAmount || 0),
      notes,
      items: items.map((it, i) => ({
        line_no: i + 1,
        product_id: it.productId,
        product_name: it.productName,
        unit: it.unit,
        quantity: Number(it.quantity || 0),
        rate: Number(it.rate || 0),
        amount: Number(it.amount || 0),
        length_breakdown: it.lengthBreakdown,
        rate_locked: it.rateLocked,
      })),
    };

    setSaving(true);
    try {
      let saved: QuotationApi;
      if (isExisting) {
        const res = await api.put<{ quotation: QuotationApi }>(
          `/quotations/${id}`,
          payload,
        );
        saved = res.quotation;
      } else {
        const res = await api.post<{ quotation: QuotationApi }>(
          "/quotations",
          payload,
        );
        saved = res.quotation;
      }
      setPostSaveNavId(saved.id);
      // Reflect the server-assigned auto number back into the form so the
      // (now read-only) input shows it while the PDF modal is open.
      const assignedNumber =
        saved.quote_number || saved.number || quotationNumber;
      if (assignedNumber) setQuotationNumber(assignedNumber);
      setStatus(coerceQuoteStatus(saved.status));
      const savedStatusLabel = quoteStatusLabel(coerceQuoteStatus(saved.status));
      const numberForToast = assignedNumber || "Quotation";
      toast.success(`Quotation ${numberForToast} saved as ${savedStatusLabel}.`);
      setCreatedByName(saved.created_by_name ?? createdByName);
      const pdf = await generatePdf(
        mapSavedQuotationToPdf(saved),
        resolvePreparedBy(saved.created_by_name ?? createdByName),
      );
      openPdfPreview(pdf, assignedNumber);
    } catch (err) {
      toast.error((err as Error).message || "Failed to save quotation");
    } finally {
      setSaving(false);
    }
  }

  const title = isPreview
    ? "View Quotation"
    : isExisting
      ? "Edit Quotation"
      : "New Quotation";
  const subtitle = lockedByWaybill
    ? "Locked — a waybill exists for this quotation. View only."
    : isPreview
      ? "Preview mode — click Edit to make changes."
      : isExisting
        ? "Edit quotation details and line items."
        : "Create a new quotation for your customer.";

  return (
    <div className="space-y-3 max-w-[1500px] text-foreground">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav("/quotations")}
            className="text-xs"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-4xl font-bold tracking-tight">{title}</h1>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && !lockedByWaybill ? (
            <Button
              variant="outline"
              className="h-8 px-3 text-xs"
              onClick={() => (id ? nav(`/quotations/${id}/edit`) : null)}
              disabled={!isPreview}
            >
              Edit
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              variant="outline"
              className="h-8 px-3 text-xs"
              onClick={startNewQuotation}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              New Quotation
            </Button>
          ) : null}
          {isExisting ? (
            <Button
              type="button"
              variant="outline"
              className="h-8 px-3 text-xs"
              onClick={handlePreviewPdfFromForm}
              disabled={loadingDoc || generatingPdf}
            >
              <FileText className="h-3.5 w-3.5 mr-1" />
              {generatingPdf ? "Preparing..." : "Preview PDF"}
            </Button>
          ) : null}
          {isPreview || isExisting ? (
            <Button
              type="button"
              variant="outline"
              className="h-8 px-3 text-xs"
              onClick={handleDownloadPdfFromForm}
              disabled={loadingDoc}
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              Download PDF
            </Button>
          ) : null}
          {canEdit && !isPreview && !lockedByWaybill && status === "draft" ? (
            <Button
              type="button"
              variant="outline"
              className="h-8 px-3 text-xs border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-300"
              onClick={() => save({ finalise: true })}
              disabled={saving || loadingDoc}
              title="Save with status Approved"
            >
              <Save className="h-4 w-4 mr-2" />
              Save & Finalise
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              className="bg-violet-500 hover:bg-violet-400 text-white h-8 px-3 text-xs"
              onClick={() => save()}
              disabled={saving || loadingDoc || lockedByWaybill || isPreview}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          ) : null}
        </div>
      </div>

      <Card className="shadow-card">
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Entity &amp; Project Information</h3>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">PFI Date</Label>
              <Input
                type="date"
                value={pfiDate}
                onChange={(e) => setPfiDate(e.target.value)}
                readOnly={lock}
                className="w-[170px] h-8 text-xs"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            {/*
              Quotation # is server-assigned and read-only. The visible input
              is intentionally narrower than the rest of the row so the field
              matches the natural width of `QTN-YYYY-####` instead of
              stretching into a 1/4-column slot.
            */}
            <div className="space-y-1.5">
              <Label>Quotation #</Label>
              <Input
                value={quotationNumber}
                readOnly
                placeholder={
                  isExisting ? "" : AUTO_QUOTE_NUMBER_PLACEHOLDER
                }
                className="h-9 font-mono w-44 max-w-full"
                aria-label="Quotation number (auto-generated)"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Customer *</Label>
              {lock ? (
                <Input value={customerName} readOnly className="h-9" />
              ) : (
                <EntityCombobox
                  aria-label="Customer entity"
                  className="h-9"
                  placeholder="Pick a saved customer entity"
                  value={customerName}
                  options={customers.map((c) => ({
                    id: c.id,
                    name: c.name,
                    secondary: c.phone || undefined,
                  }))}
                  emptyText="No saved customer entities."
                  onSelect={(opt) => {
                    const match = customers.find((c) => c.id === opt.id);
                    setCustomerName(opt.name);
                    setCustomerId(typeof opt.id === "number" ? opt.id : null);
                    if (match) {
                      setTelephone(match.phone || "");
                      if (!location) setLocation(match.address || "");
                    }
                  }}
                  onClear={() => {
                    setCustomerName("");
                    setCustomerId(null);
                  }}
                />
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Telephone</Label>
              <Input
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                readOnly={lock}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Location *</Label>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                readOnly={lock}
                className="h-9"
                placeholder="e.g. Accra"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label>Estimator</Label>
              {lock ? (
                <Input value={estimatorName} readOnly className="h-9" />
              ) : (
                <EntityCombobox
                  aria-label="Estimator entity (optional)"
                  className="h-9"
                  placeholder="Optional — pick an estimator entity"
                  value={estimatorName}
                  options={estimators.map((e) => ({
                    id: e.id,
                    name: e.name,
                    secondary: e.phone || undefined,
                  }))}
                  emptyText="No saved estimator entities."
                  onSelect={(opt) => {
                    setEstimatorName(opt.name);
                    setEstimatorId(
                      typeof opt.id === "number" ? opt.id : null,
                    );
                  }}
                  onClear={() => {
                    setEstimatorName("");
                    setEstimatorId(null);
                  }}
                />
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Attention</Label>
              {lock ? (
                <Input value={attentionName} readOnly className="h-9" />
              ) : (
                <EntityCombobox
                  aria-label="Attention entity (optional)"
                  className="h-9"
                  placeholder="Optional — pick an attention entity"
                  value={attentionName}
                  options={attentions.map((a) => ({
                    id: a.id,
                    name: a.name,
                    secondary: a.phone || undefined,
                  }))}
                  emptyText="No saved attention entities."
                  onSelect={(opt) => {
                    setAttentionName(opt.name);
                    setAttentionId(
                      typeof opt.id === "number" ? opt.id : null,
                    );
                  }}
                  onClear={() => {
                    setAttentionName("");
                    setAttentionId(null);
                  }}
                />
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Project Type *</Label>
              <Select
                value={projectType || undefined}
                onValueChange={clearable(setProjectType)}
                disabled={lock}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectNoneItem />
                  <SelectItem value="Actual">Actual</SelectItem>
                  <SelectItem value="Estimated">Estimated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={status}
                onValueChange={(value) => {
                  if (isQuoteStatus(value)) setStatus(value);
                }}
                disabled={lock}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUOTE_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {QUOTE_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isExisting && status === DEFAULT_QUOTE_STATUS ? (
                <p className="text-[11px] leading-snug text-amber-700 dark:text-amber-300">
                  New quotations start as Draft. Change the status to finalise
                  the quotation and unlock waybill creation.
                </p>
              ) : null}
            </div>
          </div>

          {/*
            Optional quotation-level attribute dropdowns (migration 0019).
            Radix Select rejects `value=""`, so we pass `undefined` while
            unset so the placeholder text shows through.
          */}
          <div className="grid md:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label>Gauge</Label>
              <Select
                value={gauge || undefined}
                onValueChange={clearable(setGauge)}
                disabled={lock}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select gauge" />
                </SelectTrigger>
                <SelectContent>
                  <SelectNoneItem />
                  {gaugeSelectOptions.map((g) => (
                    <SelectItem key={g.value} value={g.value}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Profile</Label>
              <Select
                value={profile || undefined}
                onValueChange={clearable(setProfile)}
                disabled={lock}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select profile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectNoneItem />
                  {profileSelectOptions.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Color</Label>
              <Select
                value={color || undefined}
                onValueChange={clearable(setColor)}
                disabled={lock}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select color" />
                </SelectTrigger>
                <SelectContent>
                  <SelectNoneItem />
                  {colorSelectOptions.map((co) => (
                    <SelectItem key={co.value} value={co.value}>
                      {co.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Empty cell keeps the 4-col grid balanced. */}
            <div className="hidden md:block" aria-hidden />
          </div>

          {/*
            Markup % sits on its own row so the four-column layout above
            stays visually balanced. Pinned to a fixed width matching the
            other percent inputs in the totals panel.
          */}
          <div className="grid md:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label>Markup %</Label>
              <Input
                type="number"
                value={displayNumber("markup", markupPercent)}
                onFocus={() => setActiveNumberField("markup")}
                onBlur={() => setActiveNumberField(null)}
                onChange={(e) =>
                  setMarkupPercent(Number(e.target.value || 0))
                }
                readOnly={lock}
                className="h-9 w-44 max-w-full"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-3">
        <div className="space-y-4">
          <Card className="shadow-card">
            <CardContent className="p-3 space-y-3">
              <h3 className="font-semibold">Products / Items</h3>
              <div className="grid grid-cols-[24px_2.9fr_88px_88px_36px_120px_96px_30px] gap-2 text-[11px] text-muted-foreground px-1">
                <div>#</div>
                <div>Product</div>
                <div>Unit</div>
                <div>Quantity</div>
                <div />
                <div className="inline-flex items-center gap-1">
                  Rate <Lock className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                </div>
                <div>Amount</div>
                <div />
              </div>
              <div className="space-y-2 pt-1">
                {items.map((it, i) => {
                  const breakdownInvalid =
                    !lock && isCatalogBreakdownInvalid(it, products);
                  const isFlashingDuplicate = duplicateHighlightIdx === i;
                  return (
                    <div
                      key={i}
                      ref={(node) => {
                        itemRowRefs.current[i] = node;
                      }}
                      className={`grid grid-cols-[24px_2.9fr_88px_88px_36px_120px_96px_30px] gap-2 items-center rounded-md transition-colors ${
                        isFlashingDuplicate
                          ? "bg-amber-500/15 ring-1 ring-amber-500/50"
                          : ""
                      }`}
                    >
                      <div className="text-[11px] text-muted-foreground text-center">
                        {i + 1}
                      </div>
                      {lock ? (
                        <Input
                          className="h-8 w-full"
                          value={it.productName}
                          readOnly
                        />
                      ) : (
                        <div className="space-y-1">
                          <ProductCombobox
                            value={it.productName}
                            productId={it.productId}
                            disabled={lock}
                            placeholder="Click to choose, or type to filter…"
                            // Strict: every quotation line MUST resolve to a
                            // catalog product. Free-text-only rows are blocked
                            // at save time and visually flagged in red below.
                            strict
                            invalid={
                              showProductErrors && it.productId == null
                            }
                            // Reuse the already-fetched catalog so each row's
                            // dropdown opens instantly with the full list.
                            options={products}
                            onTextChange={(name) =>
                              updateItem(i, {
                                productName: name,
                                productId: null,
                              })
                            }
                            onSelect={(p) => applyProductToRow(i, p)}
                            onClear={() =>
                              updateItem(i, {
                                productName: "",
                                productId: null,
                              })
                            }
                          />
                          {showProductErrors && it.productId == null ? (
                            <p className="text-[11px] text-destructive leading-tight">
                              Please select a product from the list.
                            </p>
                          ) : null}
                        </div>
                      )}
                      <Input
                        className="h-8"
                        value={it.unit}
                        onChange={(e) => updateItem(i, { unit: e.target.value })}
                        readOnly={lock}
                      />
                      <Input
                        className="h-8"
                        type="number"
                        step="0.01"
                        placeholder="Qty"
                        value={displayNumber(`item-qty-${i}`, it.quantity)}
                        readOnly
                      />
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className={`h-8 w-8 ${
                              breakdownInvalid
                                ? "border-amber-500/80 ring-1 ring-amber-500/50"
                                : ""
                            }`}
                            title={
                              lock
                                ? "View length × quantity"
                                : itemNeedsLengthQty(it, products)
                                  ? "Length × quantity (required)"
                                  : "Length × quantity"
                            }
                          >
                            <Link2 className="h-3.5 w-3.5" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          align="start"
                          className="w-[330px]"
                        >
                          <div className="space-y-3">
                            <p className="text-xs font-semibold">
                              Multiple Length × Quantity
                            </p>
                            {lock ? (
                              <p className="text-[11px] text-muted-foreground">
                                View only — click Edit to change values.
                              </p>
                            ) : itemNeedsLengthQty(it, products) ? (
                              <p className="text-[11px] text-amber-700 dark:text-amber-300">
                                Required for this product: length and quantity must
                                both be greater than zero.
                              </p>
                            ) : null}
                            {(it.lengthBreakdown.length
                              ? it.lengthBreakdown
                              : [{ length: 0, quantity: 0 }]
                            ).map((entry, breakdownIdx) => (
                              <div
                                key={breakdownIdx}
                                className={`grid gap-2 ${
                                  lock
                                    ? "grid-cols-2"
                                    : "grid-cols-[1fr_1fr_auto]"
                                }`}
                              >
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="Length"
                                  value={displayNumber(
                                    `length-${i}-${breakdownIdx}`,
                                    entry.length,
                                  )}
                                  onFocus={() =>
                                    setActiveNumberField(
                                      `length-${i}-${breakdownIdx}`,
                                    )
                                  }
                                  onBlur={() => setActiveNumberField(null)}
                                  onChange={(e) =>
                                    updateBreakdown(i, breakdownIdx, {
                                      length: Number(e.target.value || 0),
                                    })
                                  }
                                  readOnly={lock}
                                  className="h-8"
                                />
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="Qty"
                                  value={displayNumber(
                                    `lengthqty-${i}-${breakdownIdx}`,
                                    entry.quantity,
                                  )}
                                  onFocus={() =>
                                    setActiveNumberField(
                                      `lengthqty-${i}-${breakdownIdx}`,
                                    )
                                  }
                                  onBlur={() => setActiveNumberField(null)}
                                  onChange={(e) =>
                                    updateBreakdown(i, breakdownIdx, {
                                      quantity: Number(e.target.value || 0),
                                    })
                                  }
                                  readOnly={lock}
                                  className="h-8"
                                />
                                {!lock ? (
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    onClick={() =>
                                      removeBreakdownRow(i, breakdownIdx)
                                    }
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                ) : null}
                              </div>
                            ))}
                            <div className="flex items-center justify-between">
                              {!lock ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8"
                                  onClick={() => addBreakdownRow(i)}
                                >
                                  <Plus className="h-3.5 w-3.5 mr-1" />
                                  Add line
                                </Button>
                              ) : null}
                              <span
                                className={`text-xs text-muted-foreground ${
                                  lock ? "ml-auto w-full text-right" : ""
                                }`}
                              >
                                Computed Qty: {it.quantity}
                              </span>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                      <div className="relative">
                        <Input
                          className="h-8 pr-7"
                          type="number"
                          step="0.01"
                          placeholder="Rate"
                          value={displayNumber(`item-rate-${i}`, it.rate)}
                          onFocus={() => setActiveNumberField(`item-rate-${i}`)}
                          onBlur={() => setActiveNumberField(null)}
                          onChange={(e) =>
                            updateItem(i, { rate: Number(e.target.value || 0) })
                          }
                          readOnly={lock || it.rateLocked}
                        />
                        <button
                          type="button"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-amber-600 hover:text-amber-500 dark:text-amber-400 dark:hover:text-amber-300 disabled:opacity-50"
                          title={
                            it.rateLocked
                              ? "Unlock to override default rate"
                              : "Lock rate"
                          }
                          onClick={() =>
                            updateItem(i, { rateLocked: !it.rateLocked })
                          }
                          disabled={lock}
                        >
                          <Lock
                            className={`h-3.5 w-3.5 ${
                              it.rateLocked ? "" : "opacity-40"
                            }`}
                          />
                        </button>
                      </div>
                      <div className="text-right self-center font-semibold text-sm">
                        {formatNumber(it.amount)}
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() =>
                          setItems((prev) =>
                            prev.length > 1
                              ? prev.filter((_, idx) => idx !== i)
                              : prev,
                          )
                        }
                        disabled={lock}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  className="bg-violet-500 hover:bg-violet-400 h-9 text-white"
                  onClick={() =>
                    setItems((prev) => [...prev, { ...BLANK_ITEM }])
                  }
                  disabled={lock}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9"
                  onClick={() => setItems([{ ...BLANK_ITEM }])}
                  disabled={lock}
                >
                  Clear All
                </Button>
              </div>
              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  readOnly={lock}
                  className="min-h-[130px]"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="shadow-card h-fit">
          <CardContent className="p-5 pt-16 text-sm">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Subtotal (GHS)</span>
                <span className="font-mono font-semibold">
                  {formatNumber(subtotal)}
                </span>
              </div>

              <div className="grid grid-cols-[1fr_auto] items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-semibold text-muted-foreground leading-none">
                    Discount (%)
                  </Label>
                  <Input
                    type="number"
                    value={displayNumber("discountPercent", discountPercent)}
                    onFocus={() => setActiveNumberField("discountPercent")}
                    onBlur={() => setActiveNumberField(null)}
                    onChange={(e) =>
                      setDiscountPercent(Number(e.target.value || 0))
                    }
                    readOnly={lock}
                    className="h-8 w-[92px] text-right"
                  />
                </div>
                <span className="font-mono font-semibold">
                  {formatNumber(discountAmount)}
                </span>
              </div>

              {taxEnabled ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Taxable Amount</span>
                    <span className="font-mono font-semibold">
                      {formatNumber(taxableAmount)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">NHIL (2.5%)</span>
                    <span className="font-mono font-semibold">
                      {formatNumber(nhil)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">GETFund (2.5%)</span>
                    <span className="font-mono font-semibold">
                      {formatNumber(getfund)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">VAT (15%)</span>
                    <span className="font-mono font-semibold">
                      {formatNumber(vat)}
                    </span>
                  </div>
                </>
              ) : null}
              <div className="flex items-center justify-between">
                <span className="text-foreground font-semibold">NET TOTAL</span>
                <span className="font-mono font-semibold">
                  {formatNumber(netTotal)}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t">
              <p className="text-xs tracking-wide uppercase text-muted-foreground mb-3">
                Optional Services
              </p>
              <div className="space-y-3">
                <div className="grid grid-cols-[1fr_auto] items-center gap-3">
                  <Label className="text-base font-semibold text-muted-foreground">
                    Transportation
                  </Label>
                  <Input
                    type="number"
                    value={displayNumber("transportation", transportation)}
                    onFocus={() => setActiveNumberField("transportation")}
                    onBlur={() => setActiveNumberField(null)}
                    onChange={(e) =>
                      setTransportation(Number(e.target.value || 0))
                    }
                    readOnly={lock}
                    className="h-8 w-[128px] text-right"
                  />
                </div>

                <div className="grid grid-cols-[1fr_auto] items-start gap-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Label className="text-base font-semibold text-muted-foreground leading-none">
                        Installation
                      </Label>
                      <div className="inline-flex overflow-hidden rounded-md border">
                        <button
                          type="button"
                          disabled={lock}
                          onClick={() => switchInstallationMode("percent")}
                          className={`px-2 py-0.5 text-xs font-medium transition-colors ${
                            installationMode === "percent"
                              ? "bg-primary text-primary-foreground"
                              : "bg-transparent text-muted-foreground hover:bg-muted"
                          }`}
                          aria-pressed={installationMode === "percent"}
                          title="Enter installation as a percentage of the subtotal"
                        >
                          %
                        </button>
                        <button
                          type="button"
                          disabled={lock}
                          onClick={() => switchInstallationMode("amount")}
                          className={`border-l px-2 py-0.5 text-xs font-medium transition-colors ${
                            installationMode === "amount"
                              ? "bg-primary text-primary-foreground"
                              : "bg-transparent text-muted-foreground hover:bg-muted"
                          }`}
                          aria-pressed={installationMode === "amount"}
                          title="Enter installation as a flat amount (GHS)"
                        >
                          GHS
                        </button>
                      </div>
                    </div>
                    {installationMode === "percent" ? (
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        value={displayNumber(
                          "installationPercent",
                          installationPercent,
                        )}
                        onFocus={() =>
                          setActiveNumberField("installationPercent")
                        }
                        onBlur={() => setActiveNumberField(null)}
                        onChange={(e) =>
                          setInstallationPct(Number(e.target.value || 0))
                        }
                        readOnly={lock}
                        className="h-8 w-[92px] text-right"
                        aria-label="Installation percentage of subtotal"
                        title="Installation Amount = Subtotal × this %"
                      />
                    ) : (
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        value={displayNumber(
                          "installationAmount",
                          installationAmount,
                        )}
                        onFocus={() =>
                          setActiveNumberField("installationAmount")
                        }
                        onBlur={() => setActiveNumberField(null)}
                        onChange={(e) =>
                          setInstallationAmt(Number(e.target.value || 0))
                        }
                        readOnly={lock}
                        className="h-8 w-[128px] text-right"
                        aria-label="Installation flat amount in GHS"
                        title="Flat installation amount (GHS)"
                      />
                    )}
                  </div>
                  <span className="font-mono font-semibold">
                    {formatNumber(installationAmount)}
                  </span>
                </div>
              </div>
            </div>

            <div className="pt-3 mt-4 border-t flex items-center justify-between">
              <span className="text-3xl font-bold">Total (GHS)</span>
              <span className="text-4xl font-bold text-primary">
                {formatNumber(total)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <PdfPreviewModal
        open={pdfModalOpen}
        onOpenChange={handlePdfModalOpenChange}
        pdfUrl={pdfPreviewUrl}
        filename={pdfFilename}
        title="Quotation PDF"
        documentNumber={pdfQuoteNumber}
      />
    </div>
  );
}
